import { faAngleLeft, faAngleRight } from "@fortawesome/pro-solid-svg-icons";
import { computed, signal } from "@preact/signals-react";
import { fieldPropertyMap } from "../../Fields/PropertyTypes/FieldPropertyMap";
import math from "../../Mathjs/CustomMathjsInstance";
import { selectedFieldPropertyValues } from "../../Template/NewTemplateSignals";
import { ColorChangingIcon } from "../UI/ColorChangingIcon";

export const galleryConstructorGridRows = signal([]);
export const galleryConstructorVisibility = signal(false);
export const propertiesVisible = signal(false);
export const selectedFieldType = signal("");
export const selectedFieldName = signal("");
export const selectedFieldIndex = signal();
export const galSessionData = signal([]);

export const galleryConstructorValidationErrors = computed(() => {
  return galSessionData.value
    .filter((row) => {
      if (!row.fieldType) return true;

      const { isValid } = validateFieldName(row.fieldName || "", row.id);
      return !isValid;
    })
    .map((row) => row.id);
});

export const validateFieldName = (value, currentId = null) => {
  if (!value) {
    return { isValid: false, errorMessage: "Field Name is required." };
  }

  // Get all field names except the current one being edited
  const otherFieldNames = galSessionData.value
    .filter(({ id }) => id !== currentId)
    .map(({ fieldName }) => fieldName)
    .filter((name) => name);

  if (otherFieldNames.some((name) => name.trim().toLowerCase() === value.trim().toLowerCase())) {
    return { isValid: false, errorMessage: "Field Name must be unique." };
  }

  const firstChar = value.charAt(0);

  if (/\d/.test(firstChar)) {
    return { isValid: false, errorMessage: "Field Name cannot start with numbers." };
  }

  if (/[!@#$%^&*()_+\-=\]{};':"\\|,.<>?]/.test(firstChar)) {
    return { isValid: false, errorMessage: "Field Name cannot start with special symbols." };
  }

  if (Object.hasOwn(math, value)) {
    return {
      isValid: false,
      errorMessage: `Field Name "${value}" conflicts with an existing math function.`,
    };
  }

  return { isValid: true, errorMessage: "" };
};

export const galleryConstructorUniqueFieldNames = computed(() => {
  const nameCounts = {};

  // Count occurrences of each field name (case-insensitive)
  galSessionData.value.forEach(({ fieldName }) => {
    if (!fieldName) return;

    const normalizedName = fieldName.trim().toLowerCase();
    if (!nameCounts[normalizedName]) {
      nameCounts[normalizedName] = [];
    }
    nameCounts[normalizedName].push(fieldName);
  });

  // Return original names that appear more than once (case-insensitive)
  return Object.values(nameCounts)
    .filter((names) => names.length > 1)
    .flat();
});

export const galleryConstructorFieldProperties = computed(() => {
  return (
    galleryConstructorGridRows.value.filter(
      ({ fieldName }) => fieldName === selectedFieldName.value
    )[0]?.properties || {}
  );
});
export const selectedGalleryFieldPropertyValues = computed(() => {
  const index = galSessionData.value?.findIndex(
    ({ fieldName }) => fieldName === selectedFieldName.value
  );
  return galSessionData.value?.[index]?.properties || {};
});
export const galleryFields = computed(() => {
  return galleryConstructorGridRows.value.map(({ fieldName, fieldType }) => ({
    text: fieldName,
    math: "Fields",
    expressionText: fieldName?.replace(/\s/g, ""),
    fieldType: fieldType,
  }));
});

export const editedPropertyValues = signal({});
//#region Signals
export const gridPaneSize = signal("70%");
export const nestedPanes = computed(() => {
  return [
    {
      size: !propertiesVisible.value ? "95%" : gridPaneSize.value,
    },
    { resizable: propertiesVisible.value },
    {
      size: !propertiesVisible.value ? "5%" : "30%",
    },
  ];
});
export const valueMapData = computed(() => {
  return selectedFieldPropertyValues.value?.Data?.columns
    ? Array.from(
        new Set(
          Object.entries(selectedFieldPropertyValues.value?.Data?.columns)
            .filter(([, value]) => value.selected)
            .map(([key]) => key)
        )
      )
    : [];
});
export const maxId = computed(() =>
  Math.max(...(galSessionData.value.length > 0 ? galSessionData.value.map((item) => item.id) : [0]))
);

export const fieldProperties = computed(() => {
  if (!selectedFieldType.value) return [];
  if (!fieldPropertyMap) return [];
  return fieldPropertyMap[selectedFieldType.value].gallery.map((property) => ({
    ...property,
    hidden: !propertiesVisible.value,
  }));
});
export const items = computed(() => {
  return [
    {
      id: 0,
      renderer: function () {
        return (
          <div className="align-columns">
            <ColorChangingIcon
              icon={propertiesVisible.value ? faAngleRight : faAngleLeft}
              onClick={() => (propertiesVisible.value = !propertiesVisible.value)}
              initialPrimaryColor="#0078d4"
              initialSecondaryColor="#0078d4"
              initialSize={32}
              hoverSize={32}
              margin="10px 0 0 15px"
            />
            {propertiesVisible.value && (
              <h3 style={{ margin: "5px 0 0 0" }}>{selectedFieldName.value} Properties</h3>
            )}
          </div>
        );
      },
    },
  ].map((item) => ({ ...item }));
});
export const propertiesWithHeaders = computed(() => {
  const groupByCategory = (properties) => {
    if (!Array.isArray(properties)) return {};

    return properties?.reduce((acc, prop) => {
      if (!acc[prop.propertyCategory]) {
        acc[prop.propertyCategory] = [];
      }
      acc[prop.propertyCategory].push(prop);
      return acc;
    }, {});
  };

  const categorizedProperties = groupByCategory(fieldProperties.value);

  return Object.entries(categorizedProperties).flatMap(([category, props]) => [
    {
      isHeader: "true",
      category,
      renderer: function () {
        return (
          <div className="property-category">
            <h3>{category}</h3>
          </div>
        );
      },
    },
    ...props,
  ]);
});
//#endregion Signals
